import { useState, useEffect } from "react";
import { FiBriefcase, FiCalendar, FiCheck, FiDollarSign, FiFileText, FiHash, FiPlus, FiTag, FiUpload, FiX } from "react-icons/fi";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { API_ENDPOINTS } from "../api/apiConfig";

function WorkOrderForm({ onSave, onClose, selectedSite, initialData }) {
  const [f, setF] = useState({ woType: "Company", woNumber: "", issueDate: "", projectName: "", remarks: "", grandTotal: "" });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [message, setMessage] = useState("");

  const handleSave = () => {
    if (!f.woNumber || !f.issueDate) {
      setMessage("Please provide WO Number and Issue Date");
      setTimeout(() => setMessage(""), 3000);
      return;
    }
    const payload = {
      workOrderId: initialData?.workOrderId || initialData?.workorderId || initialData?.id || undefined,
      woType: f.woType,
      woNumber: f.woNumber,
      issueDate: f.issueDate,
      projectName: f.projectName,
      remarks: f.remarks,
      grandTotal: f.grandTotal ? Number(f.grandTotal) : 0,
      site: selectedSite || null,
      attachmentFile: attachmentFile || undefined
    };
    onSave(payload);
  };

  useEffect(() => {
    if (initialData) {
      setF({
        woType: initialData.woType || initialData.wo_type || "Company",
        woNumber: initialData.woNumber || initialData.workOrderNo || initialData.work_order_no || "",
        issueDate: initialData.issueDate ? String(initialData.issueDate).slice(0, 10) : (initialData.issue_date ? String(initialData.issue_date).slice(0, 10) : ""),
        projectName: initialData.projectName || initialData.project || "",
        remarks: initialData.remarks || initialData.remark || "",
        grandTotal: initialData.grandTotal ?? initialData.amount ?? "",
      });
      // if initialData contains a file metadata we don't set attachmentFile; user can choose a new file to replace
    }
  }, [initialData]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: "14px",
        padding: "18px",
        borderRadius: "12px",
        background: "linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%)",
        border: "1px solid #dbeafe"
      }}>
        <div style={{
          width: "46px",
          height: "46px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "12px",
          color: "#fff",
          background: "linear-gradient(135deg, #2563eb, #4f46e5)",
          boxShadow: "0 5px 12px rgba(37, 99, 235, 0.22)"
        }}>
          <FiBriefcase size={22} />
        </div>
        <div>
          <div style={{ color: "#0f172a", fontSize: "16px", fontWeight: 700 }}>
            {initialData ? "Update work order details" : "Create a new work order"}
          </div>
          <div style={{ color: "#64748b", fontSize: "13px", marginTop: "3px" }}>
            Add the work order details and attach any supporting document.
          </div>
        </div>
      </div>

      <div className="form-grid">
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiTag size={14} color="#2563eb" /> Work Order Type *</label>
          <select value={f.woType} onChange={e => setF(prev => ({ ...prev, woType: e.target.value }))}>
            <option value="Company">Company</option>
            <option value="Client">Client</option>
          </select>
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiHash size={14} color="#2563eb" /> WO Number *</label>
          <input value={f.woNumber} onChange={e => setF(prev => ({ ...prev, woNumber: e.target.value }))} placeholder="Enter work order number" />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiCalendar size={14} color="#2563eb" /> Issue Date *</label>
          <input type="date" value={f.issueDate} onChange={e => setF(prev => ({ ...prev, issueDate: e.target.value }))} />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiBriefcase size={14} color="#2563eb" /> Project Name</label>
          <input value={f.projectName} onChange={e => setF(prev => ({ ...prev, projectName: e.target.value }))} placeholder="Enter project name" />
        </div>
        <div className="field">
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiDollarSign size={14} color="#2563eb" /> Grand Total</label>
          <input type="number" min="0" step="0.01" value={f.grandTotal} onChange={e => setF(prev => ({ ...prev, grandTotal: e.target.value }))} placeholder="Enter grand total" />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiFileText size={14} color="#2563eb" /> Remarks</label>
          <textarea value={f.remarks} onChange={e => setF(prev => ({ ...prev, remarks: e.target.value }))} placeholder="Add any remarks..." rows="3" />
        </div>
        <div className="field" style={{
          gridColumn: "1 / -1",
          padding: "14px",
          border: "1px dashed #93c5fd",
          borderRadius: "10px",
          background: "#f8fbff"
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: "6px" }}><FiUpload size={14} color="#2563eb" /> Supporting Document</label>
          <input type="file" onChange={e => setAttachmentFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, borderTop: "1px solid #e2e8f0", paddingTop: 14 }}>
        <button type="button" className="btn-secondary" onClick={onClose}><FiX size={15} /> Cancel</button>
        <button type="button" className="btn-primary" onClick={handleSave}>
          {initialData ? <FiCheck size={15} /> : <FiPlus size={15} />}
          {initialData ? "Update Work Order" : "Save Work Order"}
        </button>
      </div>
      {message && <div role="alert" style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px" }}>{message}</div>}
    </div>
  );
}

export default WorkOrderForm;
